import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  LinearProgress,
} from "@mui/material";

import { WITHDRAWALS_GROUP_BY_OPTIONS } from "../utils/consts";
import {
  Grouping,
  RawData,
  Withdrawal,
  AggregatedWithdrawalData,
} from "../types";
import { buildGroupTree, capitaliseFirstLetter } from "../utils/utils";
import { renderGroupRowsWithRowSpan } from "./utils";
import { ReportTypes } from "../ReportsDashboard";

interface WithdrawalsTableProps {
  groupings: Grouping[];
  isLoading: boolean;
  isFetching: boolean;
  rawData?: RawData<Withdrawal>;
  aggregatedWithdrawalData?: AggregatedWithdrawalData[];
}

const WithdrawalsTable = ({
  groupings,
  isLoading,
  isFetching,
  rawData,
  aggregatedWithdrawalData,
}: WithdrawalsTableProps) => {
  const aggregatedColumns = ["Number of Withdrawal", "Amount (USD)"];

  const rawColumns = [
    "Withdrawal ID",
    "Client ID",
    "Withdrawal Status",
    "PSP",
    "PSP Transaction ID",
    "Currency Type",
    "Amount (USD)",
    "Local Amount",
    "Local Currency",
    "Exchange Rate",
    "Created At",
  ];

  const columns = groupings.length > 0 ? aggregatedColumns : rawColumns;

  const cellSx = {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  };
  const rawRows =
    isLoading || isFetching
      ? []
      : rawData?.map((withdrawal, idx) => {
          return (
            <TableRow key={idx}>
              <TableCell align="right" sx={cellSx}>
                {withdrawal.withdrawalId.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {withdrawal.userId.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {capitaliseFirstLetter(
                  withdrawal.transactionStatus.toLocaleString().toLowerCase()
                )}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {(withdrawal.psp || "Unknown PSP").toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {withdrawal.pspTransactionId || "-"}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {withdrawal.currencyType || "-"}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {`$${(withdrawal.amount / 100).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {`$${((withdrawal.localAmount || 0) / 100).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}`}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {withdrawal.localCurrency || "-"}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {`${
                  withdrawal.exchangeRate === null
                    ? "-"
                    : withdrawal.exchangeRate.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                }`}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {new Date(withdrawal.createdAt).toLocaleString()}
              </TableCell>
            </TableRow>
          );
        });

  return (
    <TableContainer
      component={Paper}
      sx={{ mt: 2 }}
      style={{
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "none",
      }}
    >
      <MuiTable>
        <TableHead>
          <TableRow>
            {groupings.map((g, idx) => (
              <TableCell
                key={idx}
                sx={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  textTransform: "none",
                }}
              >
                <b style={{ textTransform: "none" }}>
                  {WITHDRAWALS_GROUP_BY_OPTIONS.find(
                    (opt) => opt.value === g.groupBy
                  )?.name || g.groupBy}
                </b>
              </TableCell>
            ))}
            {columns.map((col, idx) => (
              <TableCell
                sx={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  textTransform: "none",
                }}
                key={idx}
              >
                <b style={{ textTransform: "none" }}>{col}</b>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ||
          isFetching ||
          (!aggregatedWithdrawalData && !rawData) ? (
            <TableRow sx={{ padding: 0 }}>
              <TableCell colSpan={groupings.length + 6} sx={{ padding: 0 }}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          ) : null}
          {aggregatedWithdrawalData &&
            groupings.length > 0 &&
            renderGroupRowsWithRowSpan(
              buildGroupTree(
                aggregatedWithdrawalData.filter((row) => {
                  // Split the groupKey by ' + ' to get each grouping's value
                  const keyParts = row.groupKey.split(" + ");
                  return groupings.every((g, idx) => {
                    const filter: string[] = g.groupFilter;
                    if (!filter || filter.length === 0) return true;
                    return filter.includes(keyParts[idx]);
                  });
                }),
                groupings
              ),
              groupings,
              undefined,
              ReportTypes.Withdrawals
            )}
          {rawData && groupings.length === 0 && rawRows}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default WithdrawalsTable;
